import { ColumnDef } from "@tanstack/react-table";
import { z, ZodObject, ZodRawShape } from "zod";
import { useTranslation } from "react-i18next";
import LicensePlate from "license-plate";
import licencePlateSelectBg from "../../../general/fields/modules/commons/licencePlateSelectField/assets/licencePlateSelectBg.svg";
import React from "react";
import edit from "../../../planning/packingSlip/modules/features/assets/pen-to-square-solid.svg";
import { EntitySelectField } from "../../../general/fields/modules/commons/entitySelectField/entitySelectField";
import { positionLabelSchema } from "../../../storage/position/domain/schemas/positionLabelSchema";
import { tireSetSchema } from "../schemas/tireSetSchema";

const getLatestRequest = (original: z.infer<typeof tireSetSchema>) => {
  return original.storedLatestRequest;
};

export const TireSetTableColumns: ColumnDef<z.infer<ZodObject<ZodRawShape>>>[] =
  [
    {
      id: "car.licence",
      accessorKey: "car.licence",
      header: "licence",
      meta: {
        columnAction: "/car/:id/edit",
      },
      cell: ({ row }) => {
        const { t } = useTranslation("common");
        const plate = new LicensePlate(row.original.car?.licence ?? "");

        return (
          <div className={"d-flex align-items-center gap-2"}>
            {row.original.car?.licence && (
              <div
                className={"tireset-table-licence-plate"}
                style={{
                  background: `url(${licencePlateSelectBg}) no-repeat center center / cover`,
                }}
              >
                <div>
                  {plate.valid() ? plate.pretty() : row.original.car?.licence}
                </div>
              </div>
            )}
            <div>{row.original.car?.label}</div>

            {row.original.car?.freestock_tire && (
              <span className="badge text-bg-secondary">{t("Free stock")}</span>
            )}
          </div>
        );
      },
    },
    {
      id: "storedLatestRequest.relation.name",
      accessorKey: "storedLatestRequest.relation.name",
      header: "relation",
      cell: ({ row }) => {
        const latestRequest = getLatestRequest(
          row.original as z.infer<typeof tireSetSchema>,
        );
        const name =
          latestRequest?.relation?.name ?? row.original.car?.relation?.name;
        if (!name) {
          return <></>;
        }
        return <span>{name}</span>;
      },
    },
    {
      accessorKey: "type",
      header: "type",
      cell: ({ row }) => {
        const label = row.original.type;
        const { t } = useTranslation("common");

        return t(label);
      },
    },
    {
      enableSorting: false,
      accessorKey: "tire_set.sleep_set",
      header: "sleep_set",
      cell: ({ row }) => {
        const label = row.original.sleep_set ? "Yes" : "No";
        const { t } = useTranslation("common");

        return t(label);
      },
    },
    {
      enableSorting: false,
      accessorKey: "storedLatestRequest.state",
      header: "state",
      cell: ({ row }) => {
        const latestRequest = getLatestRequest(
          row.original as z.infer<typeof tireSetSchema>,
        );
        const state = latestRequest?.state;
        const type = latestRequest?.type;
        const in_hotel = row?.original.in_hotel;

        const { t } = useTranslation("common");

        const hotelBadge = (
          <span className={"badge bg-in-hotel text-success ms-1"}>
            {t("In hotel")}
          </span>
        );
        const outHotelBadge = (
          <span className={"badge bg-danger text-danger ms-1"}>
            {t("Not in hotel")}
          </span>
        );

        if (
          state === "concept" ||
          state === "submitted" ||
          state === "canceled" ||
          state === "refused"
        ) {
          return (
            <>
              <span className={"badge bg-" + state}>
                {t(state)} - {t(type)}
              </span>
              {in_hotel && hotelBadge} {!in_hotel && outHotelBadge}
            </>
          );
        }

        return (
          <>
            <span className={"badge bg-" + state}>{t(state)}</span>
            {in_hotel && hotelBadge} {!in_hotel && outHotelBadge}
          </>
        );
      },
    },
    {
      enableSorting: true,
      id: "storedLatestRequest.pickup_and_delivery_date",
      accessorKey: "storedLatestRequest.pickup_and_delivery_date",
      header: "b_o_date",
      cell: ({ row }) => {
        const latestRequest = getLatestRequest(
          row.original as z.infer<typeof tireSetSchema>,
        );
        const dateString = latestRequest?.pickup_and_delivery_date;

        if (
          dateString === undefined ||
          dateString === "" ||
          dateString === null
        )
          return;

        const date = new Date(dateString);
        const formattedDate = new Intl.DateTimeFormat("en-GB")
          .format(date)
          .replace(/\//g, "-");

        return <span>{formattedDate}</span>;
      },
    },
    {
      enableSorting: true,
      id: "storedLatestRequest.in_out_storage",
      accessorKey: "storedLatestRequest.in_out_storage",
      header: "in_storage",
      cell: ({ row }) => {
        const latestRequest = getLatestRequest(
          row.original as z.infer<typeof tireSetSchema>,
        );

        const fallbackRequest = row?.original.latestCompletedPickupRequest;

        const requestToUse =
          latestRequest && latestRequest.type !== "deliver"
            ? latestRequest
            : fallbackRequest && fallbackRequest.type !== "deliver"
              ? fallbackRequest
              : undefined;

        if (!requestToUse) {
          return <></>;
        }

        const dateString = requestToUse.in_out_storage;

        if (
          dateString === undefined ||
          dateString === "" ||
          dateString === null
        )
          return;

        const date = new Date(dateString);
        const formattedDate = new Intl.DateTimeFormat("en-GB")
          .format(date)
          .replace(/\//g, "-");

        return <span>{formattedDate}</span>;
      },
    },
    {
      enableSorting: true,
      id: "storedLatestRequest.in_out_storage",
      accessorKey: "storedLatestRequest.in_out_storage",
      header: "out_storage",
      cell: ({ row }) => {
        const latestRequest = getLatestRequest(
          row.original as z.infer<typeof tireSetSchema>,
        );

        const fallbackRequest = row?.original.latestCompletedDeliverRequest;

        const requestToUse =
          latestRequest && latestRequest.type !== "pickup"
            ? latestRequest
            : fallbackRequest && fallbackRequest.type !== "pickup"
              ? fallbackRequest
              : undefined;

        if (!requestToUse) {
          return <></>;
        }

        const dateString = requestToUse.in_out_storage;

        if (
          dateString === undefined ||
          dateString === "" ||
          dateString === null
        )
          return;

        const date = new Date(dateString);
        const formattedDate = new Intl.DateTimeFormat("en-GB")
          .format(date)
          .replace(/\//g, "-");

        return <span>{formattedDate}</span>;
      },
    },
    {
      id: "position.id",
      accessorKey: "position.id",
      header: "location",
      enableSorting: false,
      meta: {
        columnAction: "disabled",
      },
      cell: ({ getValue, row, column }) => {
        const initialValue = getValue();
        const [value, setValue] = React.useState(initialValue);
        const [isEdit, setIsEdit] = React.useState(false);
        const [hasChanged, setHasChanged] = React.useState(false);

        // Reset edit mode when row data changes (e.g., due to filtering/searching)
        React.useEffect(() => {
          setIsEdit(false);
          setHasChanged(false);
          // Also reset value to current row's value when row changes
          setValue(initialValue);
        }, [row.id, row.index, initialValue]);

        const changeValue = (newValue: number | null) => {
          if (!hasChanged) {
            return;
          }
          if (newValue === value) {
            return;
          }

          setValue(newValue);
          if (column.columnDef.meta?.updateData !== undefined) {
            column.columnDef.meta?.updateData(row, newValue);
          }
        };

        const position = row?.original?.position;
        if (!isEdit) {
          return (
            <a
              href={"#"}
              onClick={(e) => {
                e.preventDefault();
                setIsEdit(true);
              }}
              className="position-edit-link"
            >
              <span>{position?.label}</span>
              <img src={edit} width={16} alt={"edit"} className="edit-icon" />
            </a>
          );
        }

        return (
          <EntitySelectField
            entity="Position"
            prefix="positions"
            labelPrefix={"label"}
            key={"position-" + row.index + "-" + row.id}
            name={"position-" + row.index}
            clearable={true}
            value={value as string}
            defaultValueLabel={position?.label}
            onChange={(option) => {
              setHasChanged(true);

              const newValue = option
                ? (option as { value: number }).value
                : null;
              changeValue(newValue);
            }}
            schema={positionLabelSchema}
            limit={100}
          />
        );
      },
    },
  ];
